import type { GiftCertificate } from '@bulknutrients/bulk-shared-lib/interfaces/ICart';
import { Button } from '@components/Button/Button';
import { CartPrice, QuickCartPrice } from '@components/Cart/CartPrice';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { Icons } from '@components/Icons/Icons';
import { SkeletonImage } from '@components/Placeholders/Skeleton';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useRemoveItem } from '@hooks/cart/useRemoveItem';
import { log } from '@lib/logs';
import Image from 'next/image';
import Link from 'next/link';
import { type ReactElement, useState } from 'react';

export function QuickCartGiftCertificate({
  certificate,
}: {
  certificate: GiftCertificate;
}): ReactElement {
  const removeItem = useRemoveItem({
    include: ['line_items.physical_items.options'],
  });
  const [updating, setUpdating] = useState(false);
  /**
   * We need to do this because the type is wrong otherwise
   * Putting this in state rather than a const allows us to set it to null
   * Which allows the hack of showing an empty div if null, after removed from cart.
   * This is needed because forceUpdate was here...but not working ¯\_(ツ)_/¯
   */
  const [cert, setCert] = useState<GiftCertificate | null>({
    ...certificate,
  });

  const handleRemove = async () => {
    setUpdating(true);

    try {
      await removeItem({ id: certificate.id });
    } catch (error) {
      log({ error, location: 'QuickCartGiftCertificate handleRemove' });
    } finally {
      setCert(null);
      // in case of error, set back to false so opacity resets
      setUpdating(false);
    }
  };

  if (!cert) return <div />;

  return (
    <div className={updating ? 'opacity-50' : ''}>
      <div className="grid grid-cols-[60px_auto_90px] gap-4 mx-1 my-1.5 items-center">
        <Link
          href="/giftcertificates"
          passHref
          prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
        >
          <figure
            style={{
              margin: '0px auto',
              width: 'fit-content',
            }}
          >
            <Image
              src="/images/Bulk-Nutrients-gift-certificate.webp"
              alt="gift certificate render "
              width={60}
              height={60}
              className="object-scale-down object-center rounded-2xl"
            />
          </figure>
        </Link>

        {/* Title, Options, and Price */}
        <div>
          <TextLink href="/giftcertificates" target="_top">
            {cert.name}
          </TextLink>
          <div className="flex gap-1 leading-5 text-xs">
            <span>for {cert.recipient.name}</span>
          </div>
          <QuickCartPrice retail={cert.amount} discounted={cert.amount} />
        </div>

        {/* Quantity and remove */}
        <div className="flex flex-col justify-center gap-1">
          <Button
            type="button"
            aria-label="Remove item from cart"
            buttonStyle="text"
            onClick={handleRemove}
            className="gae-remove-item text-xs text-grey-40 no-underline"
            disabled={updating}
          >
            Remove
          </Button>
        </div>
      </div>
      <HorizontalDivider className="m-0 via-grey-mid" />
    </div>
  );
}
